import { Component, Suspense, useEffect, useRef, useState, type ErrorInfo, type ReactNode } from 'react';
import { recordSection } from '@/lib/uxMetrics';

/**
 * Error boundary + Suspense wrapper for lazy-loaded sections.
 *
 * - Catches chunk-load failures so a single failed dynamic import can't
 *   crash the whole page.
 * - Shows a compact fallback with a "Try again" action. Because React.lazy
 *   caches the rejected promise, the reliable recovery is a page reload —
 *   after a fresh HTML request the new chunk URLs succeed.
 * - Non-chunk errors are re-thrown so they still surface in monitoring.
 * - When `sectionId` is provided, emits UX telemetry (skeleton→content
 *   time, stuck/errored flags) via `uxMetrics`.
 */
interface Props {
  children: ReactNode;
  suspenseFallback?: ReactNode;
  sectionLabel?: string;
  stuckAfterMs?: number;
  /** Stable identifier for UX telemetry (e.g. "home-belowfold"). */
  sectionId?: string;
}

interface State {
  error: Error | null;
}

const isChunkError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  const msg = `${e.name ?? ''} ${e.message ?? ''}`.toLowerCase();
  return (
    msg.includes('chunkloaderror') ||
    msg.includes('loading chunk') ||
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('unable to preload css') ||
    msg.includes('load failed')
  );
};

export class LazyBoundary extends Component<Props, State> {
  state: State = { error: null };
  private mountTs = typeof performance !== 'undefined' ? performance.now() : 0;

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkError(error)) {
      // eslint-disable-next-line no-console
      console.warn('[LazyBoundary] chunk load failed', {
        message: error.message,
        stack: info.componentStack,
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('[LazyBoundary] render error', error, info.componentStack);
    }
    if (this.props.sectionId) {
      recordSection({
        section: this.props.sectionId,
        skeleton_ms: Math.round(performance.now() - this.mountTs),
        chunk_ms: null,
        render_ms: null,
        stuck: false,
        errored: true,
        retries: 0,
      });
    }
  }

  private handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.error) {
      const chunk = isChunkError(this.state.error);
      return (
        <div
          role="alert"
          className="my-8 mx-auto max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-sm"
        >
          <p className="mb-2 font-semibold text-foreground">
            {chunk ? 'Could not load this section' : 'Something went wrong'}
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            {chunk
              ? 'Your connection dropped or the app was just updated. Reloading usually fixes it.'
              : 'An unexpected error occurred while rendering this section.'}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <Suspense
        fallback={
          <StuckAwareFallback
            stuckAfterMs={this.props.stuckAfterMs ?? 15_000}
            sectionId={this.props.sectionId}
            mountTs={this.mountTs}
          >
            {this.props.suspenseFallback ?? null}
          </StuckAwareFallback>
        }
      >
        <ChunkLoadedAnnouncer />
        {this.props.sectionId ? (
          <SectionMeasure sectionId={this.props.sectionId} mountTs={this.mountTs} />
        ) : null}
        {this.props.children}
      </Suspense>
    );
  }
}

function StuckAwareFallback({
  children,
  stuckAfterMs,
  sectionId,
  mountTs,
}: {
  children: ReactNode;
  stuckAfterMs: number;
  sectionId?: string;
  mountTs: number;
}) {
  const [stuck, setStuck] = useState(false);
  const reported = useRef(false);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setStuck(true);
      if (sectionId && !reported.current) {
        reported.current = true;
        recordSection({
          section: sectionId,
          skeleton_ms: Math.round(performance.now() - mountTs),
          chunk_ms: null,
          render_ms: null,
          stuck: true,
          errored: false,
          retries: 0,
        });
      }
    }, stuckAfterMs);
    return () => window.clearTimeout(id);
  }, [stuckAfterMs, sectionId, mountTs]);

  return (
    <div className="relative">
      {children}
      {stuck ? (
        <div
          role="alert"
          className="pointer-events-none absolute inset-x-0 top-6 z-10 flex justify-center px-4"
        >
          <div className="pointer-events-auto max-w-sm rounded-lg border border-border bg-card/95 p-4 text-center shadow-lg backdrop-blur-sm">
            <p className="mb-2 text-sm font-medium text-foreground">
              Still loading this section…
            </p>
            <p className="mb-3 text-xs text-muted-foreground">
              Your connection may be slow or the app was just updated.
            </p>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Reload
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ChunkLoadedAnnouncer() {
  const [msg, setMsg] = useState('Section loaded');
  useEffect(() => {
    const id = window.setTimeout(() => setMsg(''), 1000);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <span role="status" aria-live="polite" className="sr-only">
      {msg}
    </span>
  );
}

/**
 * Fires a single UX-metrics event once the suspended chunk has resolved
 * and the real content has committed. `skeleton_ms` = boundary mount →
 * first commit of real children.
 */
function SectionMeasure({
  sectionId,
  mountTs,
}: {
  sectionId: string;
  mountTs: number;
}) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const now = performance.now();
    recordSection({
      section: sectionId,
      skeleton_ms: Math.round(now - mountTs),
      chunk_ms: null,
      render_ms: 0,
      stuck: false,
      errored: false,
      retries: 0,
    });
  }, [sectionId, mountTs]);
  return null;
}
